import React from 'react';
import tokenReplace from 'dibs-token-replace';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import ContextComponent from '../ContextComponent';
import style from '../SocialNetworks/AccountSettings-style.scss';

import {
    accountSettings as accountSettingsCopy,
    connectAccounts,
    connectBoards,
    tracking,
} from './pinterest.json';

// Actions
import pinterestUtils from '../../utils/social/pinterest';

// Components
import SanitizeHtml from '../SanitizeHtml';
import PinterestIcon from 'dibs-vg/dist/react/pinterest';
import ConfirmationModal from '../ConfirmationModal';
import PinterestConnectBoardModal from './PinterestConnectBoardModal';
import PinterestConnectAccountsModal from './ConnectAccountsModal';

import ModifySocialNetworkConnectionMutation from '../SocialNetworks/ModifySocialNetworkConnectionMutation.es';

const ALLOWED_TAGS = ['br', 'a', 'span'];
const ALLOWED_ATTRIBUTES = {
    a: ['href', 'target', 'rel', 'title'],
    span: ['class'],
};

import findConnectedBoard from './findConnectedBoard';

class PinterestAccountSettings extends ContextComponent {
    constructor(props) {
        super(props);

        this._changeBoardClick = this._changeBoardClick.bind(this);
        this._connectAccountClick = this._connectAccountClick.bind(this);
        this._disconnnectClick = this._disconnnectClick.bind(this);
        this._closeConfirmModal = this._closeConfirmModal.bind(this);
        this._onConfirmDisconnect = this._onConfirmDisconnect.bind(this);

        this.state = {
            confirmModalIsOpen: false,
            connectBoardModalIsOpen: false,
            connectAccountsModalIsOpen: false,
        };
    }

    componentWillReceiveProps(nextProps) {
        this.setState(findConnectedBoard(nextProps));
    }

    _changeBoardClick(event) {
        event.preventDefault();
        pinterestUtils.track(tracking.changeBoardClicked);
        this.setState({ connectBoardModalIsOpen: true });
    }

    _connectAccountClick(event) {
        event.preventDefault();
        pinterestUtils.track(tracking.connectModalConnectAccountClicked);
        this.setState({ connectAccountsModalIsOpen: true });
    }

    _disconnnectClick(event) {
        event.preventDefault();
        this._openConfirmModal();
    }

    _onConfirmDisconnect() {
        this._closeConfirmModal();
        const { relay, userId, userSocialNetwork } = this.props;

        ModifySocialNetworkConnectionMutation.commit(relay.environment, {
            userId,
            action: 'DISCONNECT',
            userSocialNetworkId: userSocialNetwork.serviceId,
            onSuccess: () => {
                pinterestUtils.track(tracking.disconnectSuccessful);
            },
        });
    }

    _openConfirmModal() {
        this.setState({ confirmModalIsOpen: true });
    }

    _closeConfirmModal() {
        this.setState({ confirmModalIsOpen: false });
    }

    render() {
        const {
            confirmModalIsOpen,
            connectBoardModalIsOpen,
            connectAccountsModalIsOpen,
        } = this.state;
        const { clientId, userSocialNetwork, userId } = this.props;
        const hasPinterestUser = get(userSocialNetwork, 'pinterest.user.serviceId');
        const isConnected = get(userSocialNetwork, 'status') === 'CONNECTED';
        let infoContent = accountSettingsCopy.notConnectedMessage;

        const { connectedBoard, connectedBoardName } = findConnectedBoard(this.props);

        if (isConnected && hasPinterestUser) {
            const content = connectedBoard
                ? tokenReplace(accountSettingsCopy.connectedMessage, {
                    boardName: connectedBoardName,
                })
                : accountSettingsCopy.noBoardConnectedMessage;

            infoContent = (
                <div>
                    <SanitizeHtml
                        allowedTags={ALLOWED_TAGS}
                        allowedAttributes={ALLOWED_ATTRIBUTES}
                        content={content}
                    />{' '}
                    <a onClick={this._changeBoardClick} className={style.cta} href="#">
                        {accountSettingsCopy.ctaBoard}
                    </a>
                </div>
            );
        } else if (isConnected && !hasPinterestUser) {
            infoContent = accountSettingsCopy.staleAccessToken;
        }

        return (
            <div className={style.row}>
                <div className={style.content}>
                    <PinterestIcon className={style.iconPinterest} />
                    <div>
                        <div className={style.title}>{accountSettingsCopy.title}</div>
                        <div className={style.info}>{infoContent}</div>
                    </div>
                </div>
                <div className={style.ctaWrapper}>
                    {isConnected ? (
                        <a onClick={this._disconnnectClick} className={style.cta} href="#">
                            {accountSettingsCopy.disconnectCta}
                        </a>
                    ) : (
                        <a onClick={this._connectAccountClick} className={style.cta} href="#">
                            {accountSettingsCopy.connectAccountCta}
                        </a>
                    )}
                </div>
                <ConfirmationModal
                    isOpen={confirmModalIsOpen}
                    onClose={this._closeConfirmModal}
                    onCancel={this._closeConfirmModal}
                    onConfirm={this._onConfirmDisconnect}
                    header={accountSettingsCopy.confirmHeader}
                    message={accountSettingsCopy.confirmMessage}
                    confirmCta={accountSettingsCopy.confirmCta}
                    cancelCta={accountSettingsCopy.cancelCta}
                />

                <PinterestConnectAccountsModal
                    clientId={clientId}
                    copy={connectAccounts}
                    tracking={tracking}
                    userId={userId}
                    isOpen={connectAccountsModalIsOpen}
                    onClose={() => {
                        this.setState({ connectAccountsModalIsOpen: false });
                    }}
                    onSuccess={() => {
                        this.setState({ connectBoardModalIsOpen: true });
                    }}
                />

                <PinterestConnectBoardModal
                    isOpen={connectBoardModalIsOpen}
                    onClose={() => {
                        this.setState({ connectBoardModalIsOpen: false });
                    }}
                    userId={userId}
                    userSocialNetwork={userSocialNetwork}
                    copy={connectBoards}
                    tracking={tracking}
                />
            </div>
        );
    }
}

PinterestAccountSettings.propTypes = {
    userSocialNetwork: PropTypes.object,
    clientId: PropTypes.string.isRequired,
};

export default createFragmentContainer(PinterestAccountSettings, {
    userSocialNetwork: graphql`
        fragment PinterestAccountSettings_userSocialNetwork on UserSocialNetwork {
            ... on PinterestSocialNetwork {
                ...PinterestConnectBoardModal_userSocialNetwork
                serviceId
                status
                pinterest {
                    user {
                        serviceId
                    }
                    boards {
                        name
                        url
                    }
                }
                userPreferences {
                    board
                }
            }
        }
    `,
});



// WEBPACK FOOTER //
// ./assets/js/global/components/Pinterest/PinterestAccountSettings.jsx